import { Directive, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { DateUtils } from 'src/app/client/repositories';
import { IFormModel } from '../form.model';
import { InputType } from '../input-type.enum';
import { Validation } from './validation/validation';
import { DateValidators } from './validation/validators/date-validators';
import { PhoneValidators } from './validation/validators/phone-validators';

@Directive()
export class Form<T> extends Validation {
  @ViewChild('form') form?: ElementRef;

  formGroup?: FormGroup;

  private inputControls = new Array<IFormModel>();

  registerComponents(
    controls: Array<Array<IFormModel>> | Array<IFormModel>,
  ): void {
    this.formGroup = new FormGroup({});

    const allFields = new Array<IFormModel>();
    controls?.forEach((item: Array<IFormModel> | IFormModel) => {
      if (Array.isArray(item)) {
        item.forEach((subItem: IFormModel) => {
          allFields.push(subItem);
        });
      } else {
        allFields.push(item);
      }
    });

    allFields.forEach((inputField: IFormModel) => {
      this.inputControls?.push(inputField);
      this.registerControl(inputField);
    });
  }

  registerControl(item: IFormModel) {
    this.formGroup?.addControl(item.name, new FormControl());
    const validators = this.getValidatorsForControl(item);
    if (validators.length > 0) {
      this.setValidatorForControl(item.name, validators);
    }
    if (item.disabled) {
      this.disableControl(item.name);
    }
    if (item.type === InputType.Checkbox) {
      this.setValueForControl(item.name, false);
    }
  }

  private getValidatorsForControl(item: IFormModel): ValidatorFn[] {
    const validators = this.toValidatorArray(item.validators);

    if (item.type === InputType.Date) {
      if (!validators.includes(DateValidators.invalidDate)) {
        validators.push(DateValidators.invalidDate);
      }
      if (item.min) {
        validators.push(DateValidators.minDate(item.min));
      }
      if (item.max) {
        validators.push(DateValidators.maxDate(item.max));
      }
    }

    if (
      item.type === InputType.PhoneNumber &&
      !validators.includes(PhoneValidators.invalidPhone)
    ) {
      validators.push(PhoneValidators.invalidPhone);
    }

    return validators;
  }

  private toValidatorArray(
    validators?: ValidatorFn | ValidatorFn[],
  ): ValidatorFn[] {
    if (!validators) {
      return [];
    }

    return Array.isArray(validators) ? [...validators] : [validators];
  }

  setFormObject(object: T): void {
    if (object !== undefined && this.formGroup) {
      this.formGroup?.patchValue(this.castTypes(object));
    }
  }

  setValueForControl(name: string, value: any) {
    this.formGroup?.controls[name].setValue(value);
  }

  setValidatorForControl(
    name: string,
    validators: ValidatorFn | ValidatorFn[],
  ) {
    this.formGroup?.controls[name].addValidators(validators);
  }

  removeValidatorForControl(name: string) {
    this.formGroup?.controls[name].clearValidators();
  }

  disableControl(name: string) {
    this.formGroup?.controls[name].disable();
  }

  enableControl(name: string) {
    this.formGroup?.controls[name].enable();
  }

  getFormObject(): T {
    const object = {} as T;
    for (let key of Object.keys(this.formGroup?.controls!)) {
      object[key as keyof typeof object] = this.parseValueForControl(key);
    }
    return object as T;
  }

  parseValueForControl(key: string, inputType?: InputType): any {
    const inputComponentByKey = this.getInputComponentByKey(key);
    if (inputComponentByKey) {
      const valueForControl = this.getValueForControl(key);
      return this.parseInputForControl(
        valueForControl,
        inputComponentByKey.type,
      );
    }

    return this.getValueForControl(key);
  }

  getInputComponentByKey(key: string): IFormModel {
    if (this.inputControls) {
      return this.inputControls.find((item) => item.name === key)!;
    }
    return null!;
  }

  getValueForControl(name: string, allowEmptyValue: boolean = false): any {
    const value = this.formGroup?.getRawValue()?.[name];

    if (!this.isNullOrEmpty(value) || allowEmptyValue === true) {
      return value;
    }

    return null;
  }

  castTypes(object: T): any {
    const objectWithCastTypes: any = {};

    Object.keys(this.formGroup!.controls).forEach((key: string) => {
      const inputComponentByKey = this.getInputComponentByKey(key);

      objectWithCastTypes[key] = !this.isNullOrEmpty(
        object[key as keyof typeof object],
      )
        ? object[key as keyof typeof object]
        : null;

      if (inputComponentByKey && object[key as keyof typeof object]) {
        if (inputComponentByKey.type === InputType.Date) {
          objectWithCastTypes[key] = DateUtils.convertFromISODate(
            object[key as keyof typeof object],
          );
        }
      } else if (object[key as keyof typeof object]) {
        switch (typeof object[key as keyof typeof object]) {
          case 'undefined': {
            objectWithCastTypes[key] = null;
            break;
          }
          default: {
            objectWithCastTypes[key] = object[key as keyof typeof object];
          }
        }
      }
    });
    return objectWithCastTypes;
  }

  private parseInputForControl(
    formControlValue: any,
    inputType: InputType,
  ): any {
    if (inputType === InputType.Date) {
      if (!formControlValue) {
        return null;
      }
      return DateUtils.convertToISODate(formControlValue);
    }
    return formControlValue;
  }

  trigger(): void {
    this.triggerWasValidated();
  }

  get canTrySubmit(): boolean {
    return !this.wasValidated || this.formGroup?.valid!;
  }

  get canTrySubmitWithDisabledControls(): boolean {
    return !this.wasValidated || !this.formGroup?.invalid;
  }

  get valid(): boolean {
    return this.formGroup?.valid!;
  }

  public isNullOrEmpty(object: any): boolean {
    if (
      object === null ||
      object === undefined ||
      (('' + object) as string).trim() === ''
    ) {
      return true;
    }
    return false;
  }
}
